/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  activity: {
    all: ["activity"] as const,
    /**
     * Feed de `GET /api/activity`. Incluye userId: aísla caché entre cuentas.
     * (Plan 06, E5: ya no hay feed local — un solo camino, una sola clave. El
     * duplicado `activityQueryKeys` de `activity/queries.ts` se borró con él.)
     */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
  },
  powersync: {
    /** Stats de cola de subida (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** ¿Hay datos locales? (botón de limpieza en profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Catálogo completo de temas (getThemesQuery sin paginar; profile + list-dialog COMPARTEN caché). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  /** Billing de academia (F3-H, server REST — no PowerSync). Keys broad→specific. */
  billing: {
    all: ["billing"] as const,
    meSummary: () => ["billing", "me", "summary"] as const,
    meInvoices: () => ["billing", "me", "invoices"] as const,
    mePayments: () => ["billing", "me", "payments"] as const,
    methods: (org: string | undefined) => ["billing", "methods", org ?? ""] as const,
    /** Prefijo de los pagos (para invalidación jerárquica tras registrar/verificar). */
    paymentsAll: () => ["billing", "payments"] as const,
    payments: (org: string | undefined, status: string | undefined) =>
      ["billing", "payments", org ?? "", status ?? ""] as const,
    /** Prefijo de las facturas. */
    invoicesAll: () => ["billing", "invoices"] as const,
    invoices: (
      org: string | undefined,
      student: string | undefined,
      status: string | undefined,
      overdue: boolean,
    ) =>
      ["billing", "invoices", org ?? "", student ?? "", status ?? "", overdue ? "1" : "0"] as const,
    /** Prefijo de las tasas. */
    ratesAll: () => ["billing", "rates"] as const,
    rates: (org: string | undefined) => ["billing", "rates", org ?? ""] as const,
    plans: (org: string | undefined) => ["billing", "plans", org ?? ""] as const,
    discounts: (org: string | undefined) => ["billing", "discounts", org ?? ""] as const,
    // AUTONOMY: configuración de cobros por org (methods.enabled, receipt.footer)
    // + prefijos de invalidación jerárquica para las mutaciones de config.
    config: (org: string | undefined) => ["billing", "config", org ?? ""] as const,
    configAll: () => ["billing", "config"] as const,
    methodsAll: () => ["billing", "methods"] as const,
    plansAll: () => ["billing", "plans"] as const,
    discountsAll: () => ["billing", "discounts"] as const,
    // B4 · Prepago con descuento (G3): ofertas del alumno. Su prefijo se
    // invalida al aceptar una — y también las facturas, porque aceptar sustituye
    // N cuotas por una.
    mePrepayOffers: () => ["billing", "me", "prepay-offers"] as const,
    // B3 · Operación de pasarelas: panel de conciliación/drenaje y cola de
    // webhooks. `gatewaysAll` es el prefijo que se invalida al cambiar el
    // kill-switch (el estado y el nº de pagos en vuelo cambian a la vez).
    gatewaysAll: () => ["billing", "gateways"] as const,
    gateways: (org: string | undefined) => ["billing", "gateways", org ?? ""] as const,
    webhookEventsAll: () => ["billing", "webhook-events"] as const,
    webhookEvents: (org: string | undefined, status: string | undefined) =>
      ["billing", "webhook-events", org ?? "", status ?? ""] as const,
    // B4 · G5 cuadro de caja del dueño.
    cashForecast: (org: string | undefined, from: string, to: string) =>
      ["billing", "cash-forecast", org ?? "", from, to] as const,
  },
  // F3-I: CRM / captación (leads, segmentos, plantillas, analítica). Server-only.
  crm: {
    all: ["crm"] as const,
    leadsAll: () => ["crm", "leads"] as const,
    leads: (org: string | undefined, filters: object) =>
      ["crm", "leads", org ?? "", filters] as const,
    lead: (org: string | undefined, leadId: string) => ["crm", "lead", org ?? "", leadId] as const,
    segmentsAll: () => ["crm", "segments"] as const,
    segments: (org: string | undefined) => ["crm", "segments", org ?? ""] as const,
    templatesAll: () => ["crm", "templates"] as const,
    templates: (org: string | undefined) => ["crm", "templates", org ?? ""] as const,
    funnel: (org: string | undefined) => ["crm", "funnel", org ?? ""] as const,
    sources: (org: string | undefined) => ["crm", "sources", org ?? ""] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    // AUTONOMY: invitaciones EMITIDAS por una org (vista admin, revocables).
    orgInvitations: (organizationId: string) =>
      ["auth", "listOrgInvitations", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
